import React, { useState, useEffect, useRef } from 'react';
import API_URL from '../config';
import axios from 'axios';
import { showTextDelayNotice } from './shared/sendNotice';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import confetti from 'canvas-confetti';
import BulkUploadModal from './BulkUploadModal';
import FaProofModal from './FaProofModal';
import SharedNav from './SharedNav';
import Avatar from './shared/Avatar';
import StatusLabel from './shared/StatusLabel';
import RatingRing from './shared/RatingRing';
import { callNumber } from './shared/rcWidget';
import EditCandidateModal from './shared/EditCandidateModal';
import { RoleBadge, useJobRoles } from './shared/RoleField';
import RecruiterField, { reassignIfChanged } from './shared/RecruiterField';
import canReassign from './shared/canReassign';
import TextCandidateModal from './shared/TextCandidateModal';
import { canOverride, canContactCandidate } from './shared/overrideUsers';
import TimelineDrawer from './shared/TimelineDrawer';
import ClockButton from './shared/ClockButton';
import PowerDialer from './shared/PowerDialer';
import './RecruiterPortal.css';

// Same picker options as AccountsPage.js's company sentiment -- reusing its exact CSS
// classes (acc-sent-*) so the colors/shapes/borders match without redefining anything.
const SENTIMENT_COLORS = ['green', 'mustard', 'red'];

function experienceTag(candidate) {
  try {
    const profile = typeof candidate.candidate_profile === 'string'
      ? JSON.parse(candidate.candidate_profile)
      : candidate.candidate_profile;
    const trades = profile?.trade_types;
    if (trades && trades.length > 0) return trades.slice(0, 3).join(' · ');
  } catch {}
  return candidate.last_worked || '—';
}

// ---- Driver Candidates table helpers (October 2026 redesign) ----
// "Ellenwood, GA 30294" -> "Ellenwood, GA"
const shortLocation = loc => {
  if (!loc) return '';
  const m = /^(.*?,\s*[A-Z]{2})\b/.exec(loc);
  return m ? m[1] : loc.replace(/\s*\d{5}(-\d{4})?\s*$/, '');
};
// "#303 — ELLENWOOD, GA" -> "#303 — ELL"
const shortCsa = csa => {
  const parts = String(csa).split(' — ');
  return parts.length > 1 ? `${parts[0]} — ${parts.slice(1).join(' — ').slice(0, 3)}` : csa;
};

// Progress tracker: Screened, FADV, BGV, Drug, MEC, Sent -> done / wait / bad / empty, plus the
// current stage in words. Replaces the separate status columns (GCIC stays its own column).
const SENT_STATUSES = ['ready_for_interview', 'contacted', 'maybe', 'rejected', 'hired'];
function progressFor(c) {
  const low = v => String(v || '').toLowerCase();
  const scr = low(c.screening_status) === 'pass' ? 'done' : low(c.screening_status) === 'fail' || low(c.screening_status) === 'not interested' ? 'bad' : c.screening_status ? 'wait' : '';
  const fadvS = low(c.fadv_status);
  const fadv = ['completed', 'eligible'].includes(fadvS) ? 'done' : fadvS === 'ineligible' ? 'done' : fadvS === 'sent' ? 'wait' : '';
  const bg = low(c.bg_status) === 'pass' || fadvS === 'eligible' ? 'done' : low(c.bg_status) === 'fail' || fadvS === 'ineligible' ? 'bad' : low(c.bg_status) === 'pending' ? 'wait' : '';
  const drug = low(c.drug_status) === 'pass' ? 'done' : low(c.drug_status) === 'fail' ? 'bad' : low(c.drug_status) === 'pending' ? 'wait' : '';
  const physS = low(c.physical_status);
  const mec = physS === 'pass' ? 'done' : physS === 'fail' ? 'bad' : physS ? 'wait' : '';
  const sent = SENT_STATUSES.includes(c.interview_status) ? 'done' : '';
  const steps = [scr, fadv, bg, drug, mec, sent];
  let stage, tone;
  if (sent) { stage = c.interview_status === 'hired' ? 'Hired' : `Sent to client${c.company_name ? ` · ${c.company_name}` : ''}`; tone = 'green'; }
  else if (scr === 'bad') { stage = `Screening: ${c.screening_status}`; tone = 'red'; }
  else if (bg === 'bad') { stage = fadvS === 'ineligible' ? 'FADV ineligible' : 'Background check failed'; tone = 'red'; }
  else if (drug === 'bad') { stage = 'Drug test failed'; tone = 'red'; }
  else if (mec === 'bad') { stage = 'Physical failed'; tone = 'red'; }
  else if (scr === 'done' && fadv === 'done' && bg === 'done' && drug === 'done' && mec === 'done') { stage = 'Ready to send'; tone = 'green'; }
  else if (drug === 'wait' && mec === 'wait') { stage = 'Drug & physical pending'; tone = 'amber'; }
  else if (drug === 'wait') { stage = 'Drug test pending'; tone = 'amber'; }
  else if (mec === 'wait') { stage = 'Physical pending'; tone = 'amber'; }
  else if (bg === 'wait') { stage = 'Background check pending'; tone = 'amber'; }
  else if (fadv === 'wait') { stage = 'Waiting on candidate: FADV form'; tone = 'amber'; }
  else if (bg === 'done' && (!drug || !mec)) { stage = 'Needs drug & physical sent'; tone = 'grey'; }
  else if (scr === 'done' && !fadv) { stage = 'Needs FADV sent'; tone = 'grey'; }
  else if (scr === 'wait') { stage = `Screening: ${c.screening_status}`; tone = 'amber'; }
  else { stage = 'Not screened yet'; tone = 'grey'; }
  return { steps, stage, tone };
}
const STEP_NAMES = ['SCR', 'FADV', 'BGV', 'DRUG', 'MEC', 'SENT'];
function ProgressSteps({ candidate }) {
  const { steps, stage, tone } = progressFor(candidate);
  return (
    <div className="rp-progress" title={`Screened: ${candidate.screening_status || '-'} · FADV: ${candidate.fadv_status || '-'} · BGV: ${candidate.bg_status || '-'} · Drug: ${candidate.drug_status || '-'} · Physical: ${candidate.physical_status || '-'}`}>
      <div className="rp-steps">{steps.map((st, i) => <span key={i} className={`rp-step ${st}`} />)}</div>
      <div className="rp-step-labels">{STEP_NAMES.map(n => <span key={n}>{n}</span>)}</div>
      <div className={`rp-stage ${tone}`} title={stage}>{stage}</div>
    </div>
  );
}

function RecruiterPortal({ user, onLogout }) {
  const navigate = useNavigate();
  const [textCandidate, setTextCandidate] = useState(null);
  // Recruiter workflow (October 2026): "Today" call list, missed-call tags, timeline panel
  const [timelineId, setTimelineId] = useState(null);
  const [dialer, setDialer] = useState(null); // power dialer session: { queue, sourceLabel }
  const [today, setToday] = useState({ callbacks: [], newUploads: [], doneToday: 0 });
  const [callListMode, setCallListMode] = useState(false);
  const [callListRows, setCallListRows] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [csas, setCsas] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCsa, setSelectedCsa] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [faProofCandidate, setFaProofCandidate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortByPriority, setSortByPriority] = useState(false);
  const [remarksEdits, setRemarksEdits] = useState({});
  const [editingRemarks, setEditingRemarks] = useState(null);

  // New filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sentToClient, setSentToClient] = useState('all');
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [showRatingDropdown, setShowRatingDropdown] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState('');
  const [selectedSentiment, setSelectedSentiment] = useState('');
  const [selectedSourceType, setSelectedSourceType] = useState('');
  const [selectedJobRole, setSelectedJobRole] = useState('');
  const { roles: jobRoles } = useJobRoles();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [taskMap, setTaskMap] = useState({});
  const [sentimentOpen, setSentimentOpen] = useState(null);
  const ratingDropdownRef = useRef(null);

  // Books holds (October 2026): CRM companies whose customer has an invoice 7+ days overdue. Send to
  // Client still works; the customer gets masked details and a pay link, so say so here.
  const [heldCompanies, setHeldCompanies] = useState(new Set());
  useEffect(() => {
    axios.get(`${API_URL}/api/holds/companies`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setHeldCompanies(new Set(r.data || []))).catch(() => {});
  }, []);

  // Copy backstop for values shortened by CSS (email .rp-clip4, long candidate names
  // .candidate-name-truncate): a drag can't reach the hidden end of the text, so when a copy
  // starts or ends inside one and stays within that candidate's row, copy the whole value.
  // (Don't use user-select: all for this: dragging past such an element selected dozens of rows.)
  useEffect(() => {
    const CLIPPED = '.rp-clip4, .candidate-name-truncate';
    const elOf = n => (n && (n.nodeType === 1 ? n : n.parentElement)) || null;
    const onCopy = e => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const anchor = elOf(sel.anchorNode), focus = elOf(sel.focusNode);
      const a = anchor?.closest(CLIPPED), f = focus?.closest(CLIPPED);
      const el = a || f;
      if (!el || (a && f && a !== f)) return;
      const row = el.closest('tr');
      if (!row || anchor.closest('tr') !== row || focus.closest('tr') !== row) return;
      e.clipboardData.setData('text/plain', el.textContent.trim());
      e.preventDefault();
    };
    document.addEventListener('copy', onCopy);
    return () => document.removeEventListener('copy', onCopy);
  }, []);

  // Close sentiment picker on outside click -- same pattern as AccountsPage.js
  // Today call list: callbacks (missed calls not returned) + yesterday's uploads rated 5+ not
  // called yet. Refreshed every 2 minutes and right after a call or skip.
  const fetchToday = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/candidates/today`, { headers: { Authorization: `Bearer ${token}` } });
      setToday(r.data);
      return r.data;
    } catch (e) { return null; }
  };
  useEffect(() => {
    fetchToday();
    const t = setInterval(fetchToday, 120000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCallList = async (t = today) => {
    const ids = [...t.callbacks.map(c => c.candidate_id), ...t.newUploads.map(c => c.candidate_id)];
    if (ids.length === 0) { setCallListRows([]); return; }
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/candidates?all=true&ids=${ids.join(',')}`, { headers: { Authorization: `Bearer ${token}` } });
      const byId = Object.fromEntries((r.data.candidates || r.data).map(c => [c.id, c]));
      const rows = [];
      if (t.callbacks.length) rows.push({ __section: 'callbacks' }, ...t.callbacks.map(c => byId[c.candidate_id]).filter(Boolean));
      if (t.newUploads.length) rows.push({ __section: 'new' }, ...t.newUploads.map(c => byId[c.candidate_id]).filter(Boolean));
      setCallListRows(rows);
    } catch (e) { /* keep the previous list */ }
  };
  useEffect(() => {
    if (callListMode) loadCallList();
  }, [callListMode, today]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayTags = (() => {
    const m = {};
    const ago = iso => {
      const h = Math.max(0, Math.round((Date.now() - new Date(iso + (String(iso).endsWith('Z') ? '' : 'Z'))) / 3600000));
      return h < 1 ? 'just now' : h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
    };
    for (const c of today.callbacks) {
      m[c.candidate_id] = { kind: 'callback', label: `Missed ${c.missed_count > 1 ? `${c.missed_count} calls` : 'call'} · ${ago(c.last_missed)}${c.voicemail ? ' · voicemail' : ''}` };
    }
    for (const c of today.newUploads) {
      m[c.candidate_id] = { kind: 'new', label: c.retry ? 'No answer · try again' : c.carried_over ? 'Not called · carried over' : 'New · not called' };
    }
    return m;
  })();

  // Every call from the CRM is logged straight away (call_started), so the candidate leaves the
  // call list immediately instead of waiting for the 15-minute RingCentral call sync.
  const startCall = (candidate) => {
    callNumber(candidate.phone);
    const token = localStorage.getItem('token');
    axios.post(`${API_URL}/api/candidates/${candidate.id}/call-started`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => fetchToday()).catch(() => {});
  };

  // Power dialer (October 2026): only the user's own candidates (admins/GV: all, same rule as the
  // Call button), with a phone, not blacklisted.
  const dialable = c => !!c && !c.__section && !!c.phone && canContactCandidate(user, c) && !c.blacklisted && !c.phone_blacklist_reason;
  const startDialerToday = async () => {
    const t = (await fetchToday()) || today;
    const ids = [...t.callbacks.map(c => c.candidate_id), ...t.newUploads.map(c => c.candidate_id)];
    if (!ids.length) return;
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/candidates?all=true&ids=${ids.join(',')}`, { headers: { Authorization: `Bearer ${token}` } });
      const byId = Object.fromEntries((r.data.candidates || r.data).map(c => [c.id, c]));
      const queue = ids.map(id => byId[id]).filter(dialable);
      if (queue.length) setDialer({ queue, sourceLabel: "Today's call list" });
    } catch (e) { alert('Could not load the call list.'); }
  };
  const startDialerList = () => {
    const rows = (callListMode ? callListRows : sortedCandidates).filter(dialable);
    if (rows.length) setDialer({ queue: rows, sourceLabel: callListMode ? "Today's call list" : `Current list (${rows.length})` });
  };

  const skipCall = async (candidate, reason) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/candidates/${candidate.id}/call-skip`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      fetchToday();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not skip this candidate.');
    }
  };

  useEffect(() => {
    if (sentimentOpen === null) return;
    const close = () => setSentimentOpen(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [sentimentOpen]);

  // Avatar button: copies the candidate's full name (as shown) and flashes a check for a moment
  const [copiedNameId, setCopiedNameId] = useState(null);
  const copyName = async (candidate) => {
    const text = toTitleCase(candidate.candidate_name || '').trim();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
    }
    setCopiedNameId(candidate.id);
    setTimeout(() => setCopiedNameId(id => (id === candidate.id ? null : id)), 1200);
  };

  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const calculateDays = (screeningDate) => {
    if (!screeningDate) return '-';
    const screening = new Date(screeningDate);
    const today = new Date();
    const diffTime = Math.abs(today - screening);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getNYDate = () => {
    return moment().tz('America/New_York').format('YYYY-MM-DD');
  };

  const [formData, setFormData] = useState({
    csa_id: '',
    company_id: '',
    candidate_name: '',
    fedex_id: '',
    phone: '',
    email: '',
    location: '',
    last_worked: '',
    screening_date: getNYDate(),
    bg_status: '',
    drug_status: '',
    drug_test_pass_date: '',
    physical_status: '',
    gcic_status: '',
    remarks: '',
    screening_status: '',
    fadv_status: '',
    indeed_url: '',
    is_high_priority: 0,
    mountain_rating: '',
    blacklisted: false,
    blacklist_reason: '',
    fedex_history: '',
    fadv_file_transfer: '',
    job_role: ''
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ratingDropdownRef.current && !ratingDropdownRef.current.contains(e.target)) {
        setShowRatingDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrentPage(0);
    fetchCandidates(0, false);
  }, [selectedCsa, selectedCompany, dateFilter, startDate, endDate, sentToClient, selectedRatings, selectedRecruiter, selectedSentiment, selectedSourceType, selectedJobRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced server-side search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(0);
      fetchCandidates(0, false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCompanies();
    fetchCsas();
    fetchRecruiters();
    fetchTaskMap();
  }, []);

  const fetchTaskMap = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_URL}/api/tasks/by-candidate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskMap(r.data);
    } catch (e) { /* ignore */ }
  };

  const fetchCsas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/csas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCsas(response.data);
    } catch (err) {
      console.error('Error fetching CSAs:', err);
    }
  };

  const fetchCandidates = async (page = 0, append = false) => {
    if (append) setIsLoadingMore(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (selectedCsa) params.append('csaId', selectedCsa);
      else if (selectedCompany) params.append('companyId', selectedCompany);
      if (dateFilter && dateFilter !== 'all') {
        params.append('dateFilter', dateFilter);
        if (dateFilter === 'custom' && startDate && endDate) {
          params.append('startDate', startDate);
          params.append('endDate', endDate);
        }
      }
      if (sentToClient !== 'all') params.append('sentToClient', sentToClient);
      if (selectedRatings.length > 0) params.append('ratings', selectedRatings.join(','));
      if (selectedRecruiter) params.append('recruiterId', selectedRecruiter);
      if (selectedSentiment) params.append('sentiment', selectedSentiment);
      if (selectedSourceType) params.append('sourceType', selectedSourceType);
      if (selectedJobRole) params.append('jobRole', selectedJobRole);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      if (!searchTerm.trim() && page > 0) params.append('page', page);

      const queryString = params.toString();
      const response = await axios.get(`${API_URL}/api/candidates${queryString ? `?${queryString}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      const newCandidates = data.candidates || data; // backwards compat
      const newTotal = data.total ?? newCandidates.length;
      if (append) {
        setCandidates(prev => [...prev, ...newCandidates]);
      } else {
        setCandidates(newCandidates);
      }
      setTotalCount(newTotal);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching candidates:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const fetchRecruiters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/recruiters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecruiters(response.data);
    } catch (err) {
      console.error('Error fetching recruiters:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const toggleRating = (rating) => {
    setSelectedRatings(prev =>
      prev.includes(rating) ? prev.filter(r => r !== rating) : [...prev, rating]
    );
  };

  const clearAllFilters = () => {
    setSelectedCsa('');
    setSelectedCompany('');
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
    setSentToClient('all');
    setSelectedRatings([]);
    setSelectedRecruiter('');
    setSelectedSentiment('');
    setSelectedSourceType('');
    setSelectedJobRole('');
    setSearchTerm('');
    setCurrentPage(0);
  };

  const hasActiveFilters = selectedCsa || selectedCompany || dateFilter !== 'all' || sentToClient !== 'all' || selectedRatings.length > 0 || selectedRecruiter || selectedSentiment || selectedSourceType || selectedJobRole;

  const validatePhone = (phone) => {
    if (!phone) return { valid: true };
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return { valid: false, error: 'Phone number must be 10 digits' };
    for (let i = 0; i <= digits.length - 7; i++) {
      const substring = digits.substring(i, i + 7);
      if (new Set(substring).size === 1) return { valid: false, error: 'Phone number cannot have more than 6 repeating digits' };
    }
    for (let i = 0; i <= digits.length - 7; i++) {
      let isAscending = true, isDescending = true;
      for (let j = 0; j < 6; j++) {
        if (parseInt(digits[i + j + 1]) !== parseInt(digits[i + j]) + 1) isAscending = false;
        if (parseInt(digits[i + j + 1]) !== parseInt(digits[i + j]) - 1) isDescending = false;
      }
      if (isAscending || isDescending) return { valid: false, error: 'Phone number cannot have more than 6 sequential digits' };
    }
    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.csa_id && !formData.company_id) {
      alert('Please select a Company.');
      return;
    }
    const phoneValidation = validatePhone(formData.phone);
    if (!phoneValidation.valid) {
      alert(`Phone validation error: ${phoneValidation.error}`);
      return;
    }
    if (formData.drug_status === 'Pass' && !formData.drug_test_pass_date) {
      alert('Drug Test Pass Date is required when Drug Status is set to Pass.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      if (editingCandidate) {
        await axios.put(`${API_URL}/api/candidates/${editingCandidate.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        if (canReassign(user)) await reassignIfChanged(editingCandidate, formData.recruiter_id);
      } else {
        await axios.post(`${API_URL}/api/candidates`, formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowForm(false);
      setEditingCandidate(null);
      resetForm();
      fetchCandidates();
    } catch (err) {
      console.error('Error saving candidate:', err);
      alert(err.response?.data?.error || 'Error saving candidate');
    }
  };

  const handleEdit = (candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      recruiter_id: candidate.recruiter_id,
      csa_id: candidate.csa_id || '',
      company_id: candidate.company_id,
      candidate_name: candidate.candidate_name,
      fedex_id: candidate.fedex_id || '',
      phone: candidate.phone || '',
      email: candidate.email || '',
      location: candidate.location || '',
      last_worked: candidate.last_worked || '',
      screening_date: candidate.screening_date || '',
      bg_status: candidate.bg_status || '',
      drug_status: candidate.drug_status || '',
      drug_test_pass_date: candidate.drug_test_pass_date ? candidate.drug_test_pass_date.split('T')[0] : '',
      physical_status: candidate.physical_status || '',
      gcic_status: candidate.gcic_status || '',
      remarks: candidate.remarks || '',
      screening_status: candidate.screening_status || '',
      fadv_status: candidate.fadv_status || '',
      indeed_url: candidate.indeed_url || '',
      is_high_priority: candidate.is_high_priority,
      mountain_rating: candidate.mountain_rating || '',
      experience_summary: candidate.experience_summary || '',
      blacklisted: !!candidate.blacklisted,
      blacklist_reason: candidate.blacklist_reason || '',
      _wasBlacklisted: !!candidate.blacklisted,
      fedex_history: candidate.fedex_history || '',
      fadv_file_transfer: candidate.fadv_file_transfer || '',
      job_role: candidate.job_role || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/candidates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchCandidates();
    } catch (err) {
      console.error('Error deleting candidate:', err);
      alert('Error deleting candidate');
    }
  };

  const saveRemarks = async (candidateId, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/candidates/${candidateId}/remarks`, { remarks: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, remarks: value } : c));
    } catch (err) {
      console.error('Failed to save remarks:', err);
    }
  };

  const saveSentiment = async (candidateId, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/candidates/${candidateId}/sentiment`, { sentiment: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, sentiment: value } : c));
    } catch (err) {
      console.error('Failed to save sentiment:', err);
    }
    setSentimentOpen(null);
  };

  const togglePriority = async (candidate) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/candidates/${candidate.id}/toggle-priority`, { is_high_priority: !candidate.is_high_priority }, { headers: { Authorization: `Bearer ${token}` } });
      fetchCandidates();
    } catch (err) {
      console.error('Error toggling priority:', err);
    }
  };

  const sendToClient = (candidate) => {
    // Drug/physical is no longer checked here from CRM fields (removed October 2026): the FA
    // screenshot check on the server covers it, with Send anyway for the override list.
    if (!candidate.job_role && (!candidate.mountain_rating || candidate.mountain_rating < 5)) {
      alert('This candidate is rated below 5 and cannot be sent to the client. Update their rating to 5 or above first.');
      return;
    }
    if (!candidate.csa_id) {
      alert('This candidate has no CSA set and cannot be sent to the client. Update their CSA first.');
      return;
    }
    if (!candidate.fedex_id || !String(candidate.fedex_id).trim()) {
      alert('Please fill out the FedEx ID to send to client.');
      return;
    }
    // Proof of eligibility (FA screenshot) is required as of September 2026 -- opens a modal
    // instead of window.confirm(); the actual send-to-client API call happens from inside it,
    // bundled with the screenshot upload, once the recruiter attaches a file.
    // Ex/Current FedEx with "Not Transferred": no driver file exists to screenshot, so send
    // straight away without the FA proof modal (the server allows this case only).
    if (candidate.fedex_history && candidate.fadv_file_transfer === 'Not Transferred') {
      if (!window.confirm(`Send ${candidate.candidate_name} to the client? Their FADV file was not transferred, so no FA screenshot is needed.`)) return;
      sendWithoutFaProof(candidate);
      return;
    }
    setFaProofCandidate(candidate);
  };

  const sendWithoutFaProof = async (candidate) => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_URL}/api/candidates/${candidate.id}/send-to-client`, new FormData(), { headers: { Authorization: `Bearer ${token}` } });
      handleFaProofSuccess(r.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Error sending candidate to client.');
    }
  };

  const handleFaProofSuccess = (data) => {
    setFaProofCandidate(null);
    showTextDelayNotice(data);
    // No alert() here on purpose -- a blocking native dialog fired right after confetti()
    // would freeze the animation before it can render (same reason the Hired flow this was
    // copied from doesn't use an alert either). The candidate list refreshing below is the
    // visible confirmation now.
    confetti({ particleCount: 160, spread: 90, origin: { y: 0.6 }, colors: ['#1e2d5a', '#2563eb', '#16a34a', '#facc15'] });
    fetchCandidates();
  };

  const resetInterviewStatus = async (candidateId) => {
    if (!window.confirm('Remove this candidate from Ready for Interview?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/candidates/${candidateId}/reset-interview`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert('Candidate removed from interview list');
      fetchCandidates();
    } catch (err) {
      console.error('Error resetting interview status:', err);
      alert(err.response?.data?.error || 'Error resetting interview status');
    }
  };

  const resetForm = () => {
    setFormData({
      csa_id: '',
      company_id: '',
      candidate_name: '',
      fedex_id: '',
      phone: '',
      email: '',
      location: '',
      last_worked: '',
      screening_date: getNYDate(),
      bg_status: '',
      drug_status: '',
      drug_test_pass_date: '',
      physical_status: '',
      gcic_status: '',
      remarks: '',
      screening_status: '',
      fadv_status: '',
      indeed_url: '',
      is_high_priority: 0,
      mountain_rating: '',
      blacklisted: false,
      blacklist_reason: '',
      fedex_history: '',
      fadv_file_transfer: '',
      job_role: ''
    });
  };

  // Server returns candidates pre-sorted by priority DESC, then screening date DESC (most
  // recently re-dated first within a date). The sortByPriority toggle only regroups by priority;
  // Array.sort is stable, so the server's screening-date order is kept within each group.
  const sortedCandidates = sortByPriority
    ? [...candidates].sort((a, b) => (b.is_high_priority ? 1 : 0) - (a.is_high_priority ? 1 : 0))
    : candidates;

  const highPriorityCount = candidates.filter(c => c.is_high_priority).length;
  const canLoadMore = !searchTerm.trim() && candidates.length < totalCount;

  const getRatingColor = (rating) => {
    if (!rating) return '';
    if (rating >= 8) return '#2e7d32';
    if (rating >= 5) return '#f57c00';
    return '#c62828';
  };

  return (
    <div className="recruiter-portal">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="portal-content">
        <div className="portal-header">
          <div className="header-left-section">
            <h2>Driver Candidates ({sortedCandidates.length}{candidates.length !== sortedCandidates.length ? ` of ${candidates.length}` : ''})</h2>
            <span className="candidates-count-label">
              {searchTerm.trim()
                ? `${candidates.length} result${candidates.length !== 1 ? 's' : ''}`
                : `Showing ${candidates.length} of ${totalCount}`}
              {highPriorityCount > 0 && ` · ⭐ ${highPriorityCount} High Priority`}
            </span>
          </div>
          <div className="portal-actions">
            <button
              onClick={() => { resetForm(); setEditingCandidate(null); setShowForm(true); }}
              className="add-btn"
            >
              + Add Candidate
            </button>
            <button className="add-btn" onClick={() => setShowBulkUpload(true)}>
              + Bulk Add
            </button>
          </div>
        </div>

        {/* TODAY: callbacks + new uploads to call, one list (October 2026) */}
        {(() => {
          const cb = today.callbacks.length, nw = today.newUploads.length, done = today.doneToday || 0;
          const pct = done + cb + nw > 0 ? Math.round(done / (done + cb + nw) * 100) : 100;
          return (
            <div className="rp-today-bar">
              <span className="rp-today-title">Today</span>
              <span className="rp-today-stat"><b className="rp-today-red">{cb}</b> callback{cb === 1 ? '' : 's'} waiting</span>
              <span className="rp-today-stat"><b className="rp-today-blue">{nw}</b> new upload{nw === 1 ? '' : 's'} to call</span>
              <span className="rp-today-stat"><b className="rp-today-green">{done}</b> called today</span>
              <div className="rp-today-progress" title={`${pct}% done`}><div style={{ width: `${pct}%` }} /></div>
              {!dialer && (
                <>
                  <button className="add-btn rp-dial-btn" onClick={startDialerToday} disabled={cb + nw === 0} title="Call your Today list one after another">📞 Start calling</button>
                  <button className="add-btn rp-dial-btn ghost" onClick={startDialerList} title="Call everyone shown in the table below, one after another">Dial this list</button>
                </>
              )}
              <button
                className={`add-btn rp-today-toggle${callListMode ? ' on' : ''}`}
                onClick={() => setCallListMode(m => !m)}
                disabled={!callListMode && cb + nw === 0}
              >
                {callListMode ? 'Back to all candidates' : cb + nw === 0 ? 'All caught up' : 'Show my call list'}
              </button>
            </div>
          );
        })()}

        {dialer && (
          <PowerDialer
            key={dialer.sourceLabel + dialer.queue.length}
            queue={dialer.queue}
            sourceLabel={dialer.sourceLabel}
            tags={todayTags}
            renderProgress={c => <ProgressSteps candidate={c} />}
            onTimeline={id => setTimelineId(id)}
            onClose={() => { setDialer(null); fetchToday(); }}
          />
        )}

        {/* FILTER BAR */}
        <div className="filter-bar">
          <input
            type="text"
            placeholder="Search by name, phone, or email (searches all candidates)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />

          {/* Company filter */}
          <select value={selectedCompany} onChange={(e) => { setSelectedCompany(e.target.value); setSelectedCsa(''); }} className="filter-select">
            <option value="">All Companies</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>

          {/* Recruiter filter */}
          <select value={selectedRecruiter} onChange={(e) => setSelectedRecruiter(e.target.value)} className="filter-select">
            <option value="">All Recruiters</option>
            {recruiters.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>

          {/* Date filter */}
          <select value={dateFilter} onChange={(e) => { setDateFilter(e.target.value); if (e.target.value !== 'custom') { setStartDate(''); setEndDate(''); } }} className="filter-select">
            <option value="all">All Dates</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="last3Months">Last 3 Months</option>
            <option value="custom">Custom Range</option>
          </select>
          {dateFilter === 'custom' && (
            <>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="filter-date-input" placeholder="Start date" />
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="filter-date-input" placeholder="End date" />
            </>
          )}

          {/* Sent to client filter */}
          <select value={sentToClient} onChange={(e) => setSentToClient(e.target.value)} className="filter-select">
            <option value="all">All Status</option>
            <option value="yes">Sent to Client</option>
            <option value="no">Not Sent</option>
          </select>

          {/* Mountain Rating multi-select */}
          <div className="rating-filter-container" ref={ratingDropdownRef}>
            <button
              className={`rating-filter-btn ${selectedRatings.length > 0 ? 'active' : ''}`}
              onClick={() => setShowRatingDropdown(!showRatingDropdown)}
            >
              {selectedRatings.length > 0 ? `Rating: ${selectedRatings.sort((a,b)=>a-b).join(', ')}` : 'All Ratings'} ▾
            </button>
            {showRatingDropdown && (
              <div className="rating-dropdown">
                {[1,2,3,4,5,6,7,8,9,10].map(r => (
                  <label key={r} className="rating-option">
                    <input
                      type="checkbox"
                      checked={selectedRatings.includes(r)}
                      onChange={() => toggleRating(r)}
                    />
                    <span style={{ color: getRatingColor(r), fontWeight: 600 }}>{r}</span>
                  </label>
                ))}
                {selectedRatings.length > 0 && (
                  <button className="clear-ratings-btn" onClick={() => setSelectedRatings([])}>Clear</button>
                )}
              </div>
            )}
          </div>

          {/* Sentiment filter */}
          <select value={selectedSentiment} onChange={(e) => setSelectedSentiment(e.target.value)} className="filter-select">
            <option value="">All Sentiments</option>
            <option value="green">Green</option>
            <option value="mustard">Orange</option>
            <option value="red">Red</option>
            <option value="none">No Sentiment</option>
          </select>

          {/* Prior vs New filter */}
          <select value={selectedSourceType} onChange={(e) => setSelectedSourceType(e.target.value)} className="filter-select">
            <option value="">Prior + New</option>
            <option value="prior">Prior only</option>
            <option value="new">New only</option>
          </select>

          {/* Role filter (October 2026) */}
          <select value={selectedJobRole} onChange={(e) => setSelectedJobRole(e.target.value)} className="filter-select">
            <option value="">All roles</option>
            <option value="driver">Drivers</option>
            {jobRoles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
          </select>

          {/* Clear all filters */}
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>✕ Clear Filters</button>
          )}
        </div>

        <div className="candidates-table-container mtn-table-shell">
        <div className="mtn-scroll-x">
          <table className="candidates-table" style={{ minWidth: '1400px' }}>
            <thead>
              <tr>
                <th>Days</th>
                <th>Candidates</th>
                <th>Tags</th>
                <th>Owner</th>
                <th>Remarks</th>
                <th>CSA</th>
                <th>Progress</th>
                <th>Score</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Actions</th>
                <th>Resume</th>
                <th>Location</th>
                <th>Experience</th>
                <th>Drug Test Date</th>
                <th>GCIC</th>
                <th>Screening Date</th>
                <th>FedEx ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedCandidates.length === 0 ? (
                <tr>
                  <td colSpan="19" className="empty-state">
                    No candidates found.
                  </td>
                </tr>
              ) : (
                (callListMode ? callListRows : sortedCandidates).map(candidate => candidate.__section ? (
                  <tr key={`section-${candidate.__section}`} className="rp-section-row">
                    <td colSpan="19">
                      {candidate.__section === 'callbacks'
                        ? 'Callbacks: they called and we missed it, call them back first'
                        : 'New uploads to call, best score first'}
                    </td>
                  </tr>
                ) : (
                  <tr key={candidate.id} className={candidate.is_high_priority ? 'high-priority-row' : ''}>
                    <td className="days-cell"><span className="days-badge">{calculateDays(candidate.screening_date)}</span></td>
                    <td className="candidate-name-cell">
                      <span className="mtn-name-row">
                        <button
                          type="button"
                          className={`rp-avatar-copy${copiedNameId === candidate.id ? ' copied' : ''}`}
                          title="Copy full name"
                          onClick={() => copyName(candidate)}
                        >
                          <Avatar name={candidate.candidate_name} size="sm" />
                        </button>
                        {candidate.mountain_resume_url ? (
                          <a href={`/${candidate.mountain_resume_url}`} target="_blank" rel="noopener noreferrer" className="candidate-name-link candidate-name-truncate" title={candidate.candidate_name}>{toTitleCase(candidate.candidate_name)}</a>
                        ) : (
                          <span className="candidate-name-truncate" title={candidate.candidate_name}>{toTitleCase(candidate.candidate_name)}</span>
                        )}
                        <RoleBadge role={candidate.job_role} />
                      </span>
                      {todayTags[candidate.id] && (
                        <span className={`rp-task-bubble rp-call-tag ${todayTags[candidate.id].kind === 'callback' ? 'overdue' : 'new-upload'}`}>
                          {todayTags[candidate.id].label}
                        </span>
                      )}
                      {callListMode && todayTags[candidate.id] && (
                        <select className="rp-skip-select" value="" onChange={e => e.target.value && skipCall(candidate, e.target.value)} title="Take off today's call list">
                          <option value="">Skip…</option>
                          <option>Wrong number</option>
                          <option>Not interested</option>
                          <option>Already spoke</option>
                          <option>Duplicate</option>
                        </select>
                      )}
                    </td>
                    <td className="rp-tags-cell">
                      <div className="rp-tags">
                        <ClockButton onClick={() => setTimelineId(candidate.id)} />
                        <div className="acc-sentiment-cell" onClick={e => e.stopPropagation()}>
                          <button
                            className={`acc-sent-dot acc-sent-${candidate.sentiment || 'none'}`}
                            onClick={() => setSentimentOpen(sentimentOpen === candidate.id ? null : candidate.id)}
                            title="Set sentiment"
                          />
                          {sentimentOpen === candidate.id && (
                            <div className="acc-sent-picker">
                              {SENTIMENT_COLORS.map(c => (
                                <button key={c} className={`acc-sent-dot acc-sent-${c}`} onClick={() => saveSentiment(candidate.id, c)} />
                              ))}
                              <button className="acc-sent-clear" onClick={() => saveSentiment(candidate.id, null)}>✕</button>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => togglePriority(candidate)}
                          className={`priority-toggle-btn ${candidate.is_high_priority ? 'active' : ''}`}
                          title={candidate.is_high_priority ? 'Remove from High Priority' : 'Mark as High Priority'}
                        >
                          {candidate.is_high_priority ? '⭐' : '☆'}
                        </button>
                        {(candidate.blacklisted || candidate.phone_blacklist_reason) && (
                          <span className="fadv-flag-wrapper">
                            <span className="blacklist-flag">🏴</span>
                            <span className="fadv-flag-tooltip">
                              Blacklisted{candidate.blacklisted ? '' : ' (same phone as a blacklisted record)'}: {candidate.blacklisted ? candidate.blacklist_reason : candidate.phone_blacklist_reason}
                            </span>
                          </span>
                        )}
                        {(() => {
                          // Red flag: an FADV action item and/or open tasks. Click opens Tasks.
                          const t = taskMap[String(candidate.id)];
                          const hasTask = t && t.open > 0;
                          if (!candidate.fadv_action_item && !hasTask) return null;
                          const taskLine = hasTask ? (t.overdue > 0 ? `${t.open} task${t.open > 1 ? 's' : ''}, ${t.overdue} overdue` : t.today > 0 ? `${t.open} task${t.open > 1 ? 's' : ''}, due today` : `${t.open} open task${t.open > 1 ? 's' : ''}`) : null;
                          return (
                            <span className="fadv-flag-wrapper" onClick={() => navigate(`/tasks?candidateId=${candidate.id}`)} style={{ cursor: 'pointer' }}>
                              <span className="fadv-flag">🚩</span>
                              <span className="fadv-flag-tooltip">
                                {taskLine && <div><b>{taskLine}</b> (click to open)</div>}
                                {candidate.fadv_action_item && <div>FADV: {candidate.fadv_action_item}</div>}
                              </span>
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="recruiter-cell" title={candidate.recruiter_name || ''}>
                      {candidate.recruiter_name ? candidate.recruiter_name.trim().split(/\s+/)[0] : '—'}
                    </td>
                    <td className="remarks-cell">
                      {/* Shown clipped to ~9 characters by CSS (full text stays in the DOM and in the
                          instant hover pop-out, so copy still works); click to edit in the textarea. */}
                      {editingRemarks === candidate.id ? (
                        <textarea
                          className="remarks-inline"
                          autoFocus
                          value={remarksEdits[candidate.id] !== undefined ? remarksEdits[candidate.id] : (candidate.remarks || '')}
                          onChange={e => setRemarksEdits(prev => ({ ...prev, [candidate.id]: e.target.value }))}
                          onBlur={e => {
                            const val = e.target.value;
                            if (val !== (candidate.remarks || '')) saveRemarks(candidate.id, val);
                            setEditingRemarks(null);
                          }}
                          rows={2}
                          placeholder="Add note..."
                        />
                      ) : (
                        <span className="rp-remark-wrap" data-full={candidate.remarks || ''} onClick={() => setEditingRemarks(candidate.id)}>
                          <span className={`rp-remark${candidate.remarks ? '' : ' empty'}`}>
                            {candidate.remarks || 'Add note...'}
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="csa-cell" title={candidate.csa_name || ''}>
                      {candidate.csa_name ? (
                        <span className="csa-badge">{shortCsa(candidate.csa_name)}</span>
                      ) : '—'}
                    </td>
                    <td className="rp-progress-cell"><ProgressSteps candidate={candidate} /></td>
                    <td className="rating-cell"><RatingRing rating={candidate.mountain_rating || null} /></td>
                    <td className="phone-cell" title={candidate.phone || ''}>
                      {candidate.phone && canContactCandidate(user, candidate) && (
                        <button
                          type="button"
                          className="call-btn"
                          title={`Call ${candidate.candidate_name}`}
                          onClick={() => startCall(candidate)}
                        >
                          <svg viewBox="0 0 24 24"><path d="M6.6 10.8c1.4 2.8 3.7 5.1 6.5 6.5l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1L6.6 10.8z" /></svg>
                        </button>
                      )}
                      {candidate.phone && canContactCandidate(user, candidate) && (
                        <button
                          type="button"
                          className="call-btn text-btn"
                          title={`Text ${candidate.candidate_name}`}
                          onClick={() => setTextCandidate(candidate)}
                        >
                          <svg viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H8l-4 4V6c0-1.1.9-2 2-2z" /></svg>
                        </button>
                      )}
                      <span className="rp-phone">{candidate.phone ? candidate.phone.replace(/\D/g, '') : ''}</span>
                    </td>
                    <td
                      className="email-cell"
                      style={candidate.email?.includes('indeedemail.com') ? { color: 'red', fontWeight: 'bold' } : {}}
                      title={candidate.email}
                    >
                      <span className="rp-clip4">{candidate.email}</span>
                    </td>
                    <td className="actions-cell">
                      <button onClick={() => handleEdit(candidate)} className="mtn-btn mtn-btn-edit"><span className="mtn-ic">✏</span>Edit</button>
                      {candidate.interview_status === 'ready_for_interview' ? (
                        <button onClick={() => resetInterviewStatus(candidate.id)} className="mtn-btn mtn-btn-neutral mtn-btn-action-fixed">
                          <span className="mtn-ic">↑</span>Retract
                        </button>
                      ) : candidate.interview_status && candidate.interview_status !== 'pending' ? (
                        <button className="mtn-btn mtn-btn-neutral mtn-btn-disabled mtn-btn-action-fixed" disabled title="Client has already taken action">
                          Sent (Locked)
                        </button>
                      ) : (
                        <button onClick={() => sendToClient(candidate)} className="mtn-btn mtn-btn-send mtn-btn-action-fixed"><span className="mtn-ic">→</span>Send to Client</button>
                      )}
                      {heldCompanies.has(candidate.company_id) && (
                        <span className="rp-hold-tag" title="This customer has an overdue invoice. Candidates you send are saved, and the customer sees masked details with a pay link until it's paid.">Customer on hold</span>
                      )}
                    </td>
                    <td className="resume-cell">
                      {candidate.indeed_url ? (
                        <a href={candidate.indeed_url} target="_blank" rel="noopener noreferrer" className="mtn-btn mtn-btn-primary">Resume</a>
                      ) : (
                        <button className="mtn-btn mtn-btn-primary mtn-btn-disabled" disabled>Resume</button>
                      )}
                    </td>
                    <td className="location-cell" title={candidate.location || ''}>{shortLocation(candidate.location)}</td>
                    <td className="experience-cell">{experienceTag(candidate)}</td>
                    <td className="date-cell">{candidate.drug_test_pass_date ? new Date(candidate.drug_test_pass_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                    <td className="status-cell"><StatusLabel value={candidate.gcic_status} /></td>
                    <td className="date-cell">{candidate.screening_date}</td>
                    <td className="fedex-id-cell">{candidate.fedex_id || ''}</td>
                    <td className="actions-cell">
                      <button onClick={() => handleDelete(candidate.id)} className="mtn-btn mtn-btn-delete mtn-btn-icon-only" title="Delete">🗑</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </div>

        {canLoadMore && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <button
              className="load-more-btn"
              onClick={() => fetchCandidates(currentPage + 1, true)}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading…' : `Load More (${totalCount - candidates.length} remaining)`}
            </button>
          </div>
        )}
      </div>

      {showForm && (
        <EditCandidateModal
          extraFields={editingCandidate && canReassign(user) ? (
            <RecruiterField value={formData.recruiter_id} currentId={editingCandidate.recruiter_id} currentName={editingCandidate.recruiter_name}
              onChange={v => setFormData(f => ({ ...f, recruiter_id: v }))} />
          ) : null}
          mode={editingCandidate ? 'edit' : 'add'}
          formData={formData}
          setFormData={setFormData}
          companies={companies}
          csas={csas}
          onSubmit={handleSubmit}
          onCancel={() => setShowForm(false)}
          canRemoveBlacklist={canOverride(user)}
        />
      )}

      {showBulkUpload && (
        <BulkUploadModal
          companies={companies}
          csas={csas}
          onClose={() => setShowBulkUpload(false)}
          onComplete={() => { fetchCandidates(0, false); }}
        />
      )}

      {timelineId && <TimelineDrawer candidateId={timelineId} user={user} onClose={() => setTimelineId(null)} onContact={fetchToday} />}

      {textCandidate && (
        <TextCandidateModal candidate={textCandidate} onClose={() => setTextCandidate(null)} />
      )}

      {faProofCandidate && (
        <FaProofModal
          candidate={faProofCandidate}
          onClose={() => setFaProofCandidate(null)}
          onSuccess={handleFaProofSuccess}
        />
      )}
    </div>
  );
}

export default RecruiterPortal;
